import React, { useState, useEffect } from 'react';
import { X, Users, MessageCircle, Clock, MapPin, Edit2, Trash2, Save, Search } from 'lucide-react';
import { listContactsCore } from '@gv/core';
import { updateEvent, deleteEvent, createEvent, listEvents, getEventOwnerContactId } from '../lib/events';
import { supabase } from '../lib/supabase';
import { CONTACT_MAP_TOKEN } from '../lib/transcriptMapping';
import NotificationToast from './NotificationToast';
import TranscriptBubbles from './TranscriptBubbles';

interface TranscriptParticipant {
    contactId?: string;
    name: string;
    avatar?: string | null;
}

interface TranscriptViewProps {
    event: {
        id: string;
        title: string;
        description?: string;
        location?: string;
        due_date: string;
        notes?: string;
        participants?: Array<{ contactId?: string; name: string; avatar?: string | null }>;
        customFields?: Record<string, any>;
    };
    isOpen: boolean;
    onClose: () => void;
    onContactSelect?: (contactId: string) => void;
    currentUserId?: string;
}

interface ChatMessage {
    speaker: string;
    text: string;
    timestamp?: number;
    participantData?: TranscriptParticipant;
    isSpeakerOne: boolean;
}

export default function TranscriptView({ event, isOpen, onClose, onContactSelect }: TranscriptViewProps) {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [participants, setParticipants] = useState<TranscriptParticipant[]>([]);
    const [loading, setLoading] = useState(true);

    const [isEditing, setIsEditing] = useState(false);
    const [editingSpeakerMap, setEditingSpeakerMap] = useState<Record<string, string>>({});
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [selectedSpeaker, setSelectedSpeaker] = useState<string | null>(null);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const [contactOverrides, setContactOverrides] = useState<Record<string, { contactId: string; avatar: string | null; displayName: string }>>({});
    const [originalParticipantContactIds, setOriginalParticipantContactIds] = useState<Set<string>>(new Set());
    const CONTACT_MAP_TOKEN = '---GV_CONTACT_MAP---';

    useEffect(() => {
        if (!event || !isOpen) return;
        parseTranscriptData();
    }, [event, isOpen]);

    const parseTranscriptData = async () => {
        setLoading(true);
        try {
            console.log('[TranscriptView] Complete event data received:', JSON.stringify(event, null, 2));
            const rawText = event.notes || event.description || '';
            let transcriptText = rawText;
            const tokenIndex = rawText.indexOf(CONTACT_MAP_TOKEN);
            if (tokenIndex !== -1) {
                const mainPart = rawText.substring(0, tokenIndex).trim();
                const tokenPart = rawText.substring(tokenIndex + CONTACT_MAP_TOKEN.length).trim();
                transcriptText = mainPart;
                try {
                    const jsonStart = tokenPart.indexOf('{');
                    if (jsonStart !== -1) {
                        const parsed = JSON.parse(tokenPart.substring(jsonStart));
                        if (parsed && typeof parsed === 'object') setContactOverrides(parsed); else setContactOverrides({});
                    }
                } catch (e) {
                    console.warn('Failed to parse contact map token', e);
                    setContactOverrides({});
                }
            } else {
                setContactOverrides({});
            }

            const lines = transcriptText.split('\n').filter(l => l.trim());
            const parsedMessages: ChatMessage[] = [];
            const speakerOrder: string[] = [];
            const speakerSet = new Set<string>();
            for (const line of lines) {
                const match = line.match(/^([^:]+):\s*(.+)$/);
                if (match) {
                    const [, speaker, text] = match;
                    const cleanSpeaker = speaker.trim();
                    if (!speakerSet.has(cleanSpeaker)) { speakerOrder.push(cleanSpeaker); speakerSet.add(cleanSpeaker); }
                    parsedMessages.push({ speaker: cleanSpeaker, text: text.trim(), isSpeakerOne: speakerOrder[0] === cleanSpeaker });
                }
            }

            // GET SAVED PARTICIPANTS: User manually selected these during transcription
            // Try multiple sources for participant data - transcript events store participants in different places
            const savedParticipants = event.participants || event.customFields?.chat?.participants || [];
            console.log('[TranscriptView] Saved participants from event:', savedParticipants);
            console.log('[TranscriptView] Event.participants:', event.participants);
            console.log('[TranscriptView] Event.customFields?.chat?.participants:', event.customFields?.chat?.participants);
            console.log('[TranscriptView] Full customFields:', event.customFields);

            // For transcript events, we need to ensure we get ALL participants, not just the event owner
            // This is critical for proper speaker separation in multi-participant transcripts
            let participantData: TranscriptParticipant[] = [];
            
            if (savedParticipants && savedParticipants.length > 1) {
                // Use saved participants if we have multiple (complete data)
                participantData = savedParticipants.map((p: any) => ({
                    contactId: p.contactId,
                    name: p.name,
                    avatar: p.avatar
                }));
                console.log('[TranscriptView] Using saved participants (multiple found):', participantData.length);
            } else {
                // Single participant or no participants - extract from transcript itself
                console.log('[TranscriptView] Single/no participants found, extracting from transcript text');
                
                // Extract unique speakers from transcript text
                const speakers = new Set<string>();
                const lines = transcriptText.split('\n').filter(l => l.trim());
                for (const line of lines) {
                    const match = line.match(/^([^:]+):\s*(.+)$/);
                    if (match) {
                        speakers.add(match[1].trim());
                    }
                }
                
                console.log('[TranscriptView] Found speakers in transcript text:', Array.from(speakers));
                
                // Try to get speaker mapping from custom fields
                const speakerMap = event.customFields?.speakerMap || {};
                console.log('[TranscriptView] Available speakerMap:', speakerMap);
                
                // Create participants from extracted speakers
                participantData = Array.from(speakers).map(speaker => {
                    // Try to find contact ID from speaker map or saved participants
                    let contactId = speakerMap[speaker];
                    let avatar = undefined;
                    
                    // Also check if this speaker matches any saved participant
                    if (savedParticipants && savedParticipants.length > 0) {
                        const matchedParticipant = savedParticipants.find((p: any) => 
                            p.name === speaker || p.name?.toLowerCase() === speaker.toLowerCase()
                        );
                        if (matchedParticipant) {
                            contactId = matchedParticipant.contactId;
                            avatar = matchedParticipant.avatar;
                        }
                    }
                    
                    return {
                        name: speaker,
                        contactId: contactId || undefined,
                        avatar: avatar || undefined
                    };
                });
                
                console.log('[TranscriptView] Reconstructed participants from transcript:', participantData);
            }

            if (participantData.length === 0) {
                console.warn('[TranscriptView] No participants found - creating fallback participant');
                participantData = [{ name: 'Unknown Participant', contactId: undefined, avatar: undefined }];
            }

            console.log('[TranscriptView] Using participants:', participantData);

            const messagesWithParticipants = parsedMessages.map(m => ({ ...m, participantData: participantData.find(p => p.name === m.speaker) }));
            setMessages(messagesWithParticipants);
            setParticipants(participantData);
        } catch (e) {
            console.error('Error parsing transcript data:', e);
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = () => {
        setIsEditing(true);
        const map: Record<string, string> = {};
        participants.forEach(p => { map[p.name] = p.name; });
        setEditingSpeakerMap(map);

        // Capture original participant contact IDs
        const originalContactIds = new Set<string>();
        participants.forEach(p => {
            if (p.contactId) {
                originalContactIds.add(p.contactId);
            }
        });
        setOriginalParticipantContactIds(originalContactIds);
    };

    const handleDeleteEvent = async () => {
        try {
            await deleteEvent(event.id);
            window.dispatchEvent(new CustomEvent('gv:refreshEvents', { detail: { eventId: event.id } }));
            window.dispatchEvent(new CustomEvent('gv:refreshHistory', { detail: { eventId: event.id } }));
            onClose();
        } catch (error) {
            console.error('Failed to delete event:', error);
            setNotification({ type: 'error', message: 'Failed to delete transcript' });
        }
    };

    const handleSaveChanges = async () => {
        try {
            const rawText = event.notes || event.description || '';
            const tokenIndex = rawText.indexOf(CONTACT_MAP_TOKEN);
            const baseText = tokenIndex !== -1 ? rawText.substring(0, tokenIndex).trim() : rawText;
            const lines = baseText.split('\n').filter(l => l.trim());
            const updatedLines = lines.map(line => {
                const match = line.match(/^([^:]+):\s*(.+)$/);
                if (match) {
                    const [, originalSpeaker, text] = match;
                    return `${editingSpeakerMap[originalSpeaker.trim()] || originalSpeaker.trim()}: ${text}`;
                }
                return line;
            });
            const transformedOverrides: Record<string, { contactId: string; avatar: string | null; displayName: string }> = {};
            Object.entries(contactOverrides).forEach(([orig, data]) => {
                const newName = editingSpeakerMap[orig] || orig;
                transformedOverrides[newName] = { ...data, displayName: data.displayName || newName };
            });
            const payload = JSON.stringify(transformedOverrides);
            const updatedNotes = `${updatedLines.join('\n')}\n\n${CONTACT_MAP_TOKEN}\n${payload}`;

            // Determine current participant contact IDs after editing
            const currentContactIds = new Set<string>();
            participants.forEach(p => {
                if (p.contactId) {
                    currentContactIds.add(p.contactId);
                }
            });

            // Find participants who were removed (in original but not in current)
            const removedContactIds = Array.from(originalParticipantContactIds).filter(
                id => !currentContactIds.has(id)
            );

            // Find participants who were added (in current but not in original)
            const addedContactIds = Array.from(currentContactIds).filter(
                id => !originalParticipantContactIds.has(id)
            );

            // Update the main event
            await updateEvent(event.id, { ...event, notes: updatedNotes });

            // Handle cross-contact event management if participants changed
            if (removedContactIds.length > 0 || addedContactIds.length > 0) {
                console.log('Transcript participants changed - managing cross-contact events', {
                    removed: removedContactIds,
                    added: addedContactIds
                });

                // Remove transcript events from contacts who are no longer participants
                for (const removedContactId of removedContactIds) {
                    try {
                        const contactEvents = await listEvents(removedContactId);

                        // Find matching transcript event by multiple criteria
                        const matchingEvent = contactEvents.find(evt =>
                            evt.id === event.id || // Direct ID match
                            (
                                evt.customFields?.eventType === 'Transcribed' &&
                                evt.name?.includes('Call Transcript') &&
                                evt.notes?.includes(updatedLines[0]) // Match first line of transcript
                            )
                        );

                        if (matchingEvent) {
                            console.log(`Removing transcript event ${matchingEvent.id} from contact ${removedContactId}`);
                            await deleteEvent(matchingEvent.id);
                        }
                    } catch (error) {
                        console.error(`Failed to remove transcript event from contact ${removedContactId}:`, error);
                    }
                }

                // Add transcript events to newly added participants
                for (const addedContactId of addedContactIds) {
                    try {
                        const newEventData = {
                            ...event,
                            notes: updatedNotes,
                            customFields: {
                                ...event.customFields,
                                eventType: 'Transcribed'
                            }
                        };

                        console.log(`Creating transcript event for new participant ${addedContactId}`);
                        await createEvent(addedContactId, newEventData as any);
                    } catch (error) {
                        console.error(`Failed to create transcript event for contact ${addedContactId}:`, error);
                    }
                }
            }

            parseTranscriptData();
            setIsEditing(false);
            setOriginalParticipantContactIds(new Set()); // Reset original tracking
            window.dispatchEvent(new CustomEvent('gv:refreshEvents', { detail: { eventId: event.id } }));
            window.dispatchEvent(new CustomEvent('gv:refreshHistory', { detail: { eventId: event.id } }));
            setNotification({ type: 'success', message: 'Transcript changes saved and participant events updated.' });
        } catch (error) {
            console.error('Failed to save changes:', error);
            setNotification({ type: 'error', message: 'Failed to save transcript changes.' });
        }
    };

    const searchContacts = async (query: string) => {
        if (!query.trim()) { setSearchResults([]); return; }
        try { const results = await listContactsCore(supabase as any, { query: query.trim(), limit: 10 }); setSearchResults(results || []); } catch { setSearchResults([]); }
    };

    const assignSpeakerToContact = (speaker: string, contact: any) => {
        const contactName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
        setEditingSpeakerMap(prev => ({ ...prev, [speaker]: contactName }));
        setContactOverrides(prev => ({ ...prev, [speaker]: { contactId: contact.id, avatar: contact.profile_image_url || null, displayName: contactName } }));
        setParticipants(prev => prev.map(p => p.name === speaker ? { ...p, contactId: contact.id, name: contactName, avatar: contact.profile_image_url || null } : p));
        setMessages(prev => prev.map(m => m.speaker === speaker ? { ...m, participantData: { contactId: contact.id, name: contactName, avatar: contact.profile_image_url || null }, } : m));
        setSelectedSpeaker(null); setSearchQuery(''); setSearchResults([]);
    };

    const formatTimestamp = (timestamp?: number) => {
        if (!timestamp) return ''; return new Date(timestamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const getAvatarInitials = (name: string) => {
        const names = name.split(' ');
        return names.length >= 2 ? `${names[0][0]}${names[1][0]}`.toUpperCase() : name.substring(0, 2).toUpperCase();
    };

    const handleParticipantClick = (participant: TranscriptParticipant) => {
        if (participant.contactId && onContactSelect) onContactSelect(participant.contactId);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-4xl h-[85vh] bg-gray-900 border border-gray-800 rounded-xl shadow-2xl overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-800 bg-gray-900/95 backdrop-blur">
                    <div className="flex items-center gap-3">
                        <MessageCircle className="w-5 h-5 text-blue-400" />
                        <div>
                            <h2 className="text-white font-semibold">{event.title}</h2>
                            <div className="flex items-center gap-3 text-sm text-gray-400">
                                {event.location && <div className="flex items-center gap-1"><MapPin className="w-3 h-3" /><span>{event.location}</span></div>}
                                <div className="flex items-center gap-1"><Clock className="w-3 h-3" /><span>{event.due_date && !isNaN(Date.parse(event.due_date)) ? new Date(event.due_date).toLocaleDateString() : 'Date not available'}</span></div>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {isEditing ? (
                            <>
                                <button onClick={() => { setIsEditing(false); setOriginalParticipantContactIds(new Set()); }} className="p-2 rounded border border-gray-700 text-gray-300 hover:bg-gray-800 transition-colors" aria-label="Cancel"><X className="w-4 h-4" /></button>
                                <button onClick={handleSaveChanges} className="p-2 rounded border border-green-700 text-green-300 hover:bg-green-800 transition-colors" aria-label="Save Changes"><Save className="w-4 h-4" /></button>
                                <button onClick={() => setShowConfirmDelete(true)} className="p-2 rounded border border-red-700 text-red-300 hover:bg-red-800 transition-colors" aria-label="Delete Transcript"><Trash2 className="w-4 h-4" /></button>
                            </>
                        ) : (
                            <>
                                <button onClick={handleEditClick} className="p-2 rounded border border-blue-700 text-blue-300 hover:bg-blue-800 transition-colors" aria-label="Edit Transcript"><Edit2 className="w-4 h-4" /></button>
                                <button onClick={onClose} className="p-2 rounded border border-gray-700 text-gray-300 hover:bg-gray-800 transition-colors" aria-label="Close"><X className="w-4 h-4" /></button>
                            </>
                        )}
                    </div>
                </div>

                {/* Participants */}
                <div className="border-b border-gray-800 bg-gray-800/50">
                    <div className="flex items-center gap-3 p-3">
                        <div className="flex items-center gap-2"><Users className="w-4 h-4 text-gray-400" /><span className="text-sm text-gray-300 font-medium">{isEditing ? 'Edit Participants' : 'Participants'}</span></div>
                    </div>
                    {isEditing ? (
                        <div className="p-3 space-y-3">
                            {participants.map((participant, index) => (
                                <div key={index} className="bg-gray-900 rounded-lg p-3">
                                    <div className="flex items-center justify-between mb-2"><span className="text-sm font-medium text-gray-300">Speaker {index + 1}</span>
                                        <button onClick={() => setSelectedSpeaker(selectedSpeaker === participant.name ? null : participant.name)} className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700">Change</button></div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center overflow-hidden">
                                            {participant.avatar ? <img src={participant.avatar} alt={participant.name} className="w-full h-full object-cover" /> : <span className="text-xs font-medium text-gray-300">{getAvatarInitials(editingSpeakerMap[participant.name] || participant.name)}</span>}
                                        </div>
                                        <span className="text-sm text-white">{editingSpeakerMap[participant.name] || participant.name}</span>
                                    </div>
                                </div>
                            ))}
                            {selectedSpeaker && (
                                <div className="bg-gray-900 rounded-lg p-3 border border-blue-600">
                                    <div className="flex items-center justify-between mb-3"><span className="text-sm font-medium text-blue-300">Assign "{selectedSpeaker}" to:</span><button onClick={() => { setSelectedSpeaker(null); setSearchQuery(''); setSearchResults([]); }} className="text-gray-400 hover:text-white"><X className="w-4 h-4" /></button></div>
                                    <div className="relative mb-3"><Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" /><input type="text" value={searchQuery} onChange={e => { setSearchQuery(e.target.value); searchContacts(e.target.value); }} placeholder="Search contacts..." className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-blue-500" /></div>
                                    <div className="max-h-40 overflow-y-auto space-y-1">
                                        {searchResults.map(contact => (
                                            <button key={contact.id} onClick={() => assignSpeakerToContact(selectedSpeaker, contact)} className="w-full flex items-center gap-3 p-2 rounded hover:bg-gray-800 text-left">
                                                <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center overflow-hidden">{contact.profile_image_url ? <img src={contact.profile_image_url} alt={contact.first_name} className="w-full h-full object-cover" /> : <span className="text-xs font-medium text-gray-300">{getAvatarInitials(`${contact.first_name || ''} ${contact.last_name || ''}`)}</span>}</div>
                                                <div><div className="text-sm font-medium text-white">{contact.first_name} {contact.last_name}</div>{contact.email && <div className="text-xs text-gray-400">{contact.email}</div>}</div>
                                            </button>
                                        ))}
                                        {searchQuery && !searchResults.length && <div className="text-sm text-gray-400 text-center py-2">No contacts found</div>}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-wrap items-center gap-2 p-3">
                            {participants.map((participant, i) => (
                                <button key={i} onClick={() => handleParticipantClick(participant)} disabled={!participant.contactId} title={participant.contactId ? 'Click to view contact profile' : 'No contact linked'} className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors ${participant.contactId ? 'border-blue-600 bg-blue-600/10 text-blue-300 hover:bg-blue-600/20 cursor-pointer' : 'border-gray-700 bg-gray-800 text-gray-400 cursor-default'}`}>
                                    <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center overflow-hidden">{participant.avatar ? <img src={participant.avatar} alt={participant.name} className="w-full h-full object-cover" /> : <span className="text-xs font-medium text-gray-300">{getAvatarInitials(participant.name)}</span>}</div>
                                    <span className="text-sm font-medium">{participant.name}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-950">
                    {loading ? (
                        <div className="flex items-center justify-center h-full"><div className="text-gray-400">Loading transcript...</div></div>
                    ) : !messages.length ? (
                        <div className="flex items-center justify-center h-full"><div className="text-center text-gray-400"><MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" /><p>No transcript messages found</p><p className="text-sm">Transcript data should be in the event description or notes</p></div></div>
                    ) : (
                        <TranscriptBubbles
                            segments={messages.map(m => ({ speaker: m.speaker, text: m.text }))}
                            map={isEditing ? editingSpeakerMap : undefined}
                            participants={participants.map(p => ({
                                contactId: p.contactId,
                                name: p.name,
                                avatar: p.avatar
                            }))}
                            className="flex-1 overflow-y-auto"
                            onParticipantClick={handleParticipantClick}
                        />
                    )}
                </div>

                <div className="p-3 border-t border-gray-800 bg-gray-900/95 backdrop-blur"><div className="text-xs text-gray-400 text-center">Transcript • {messages.length} messages • {participants.length} participants</div></div>
            </div>

            {showConfirmDelete && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-10">
                    <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 max-w-sm w-full">
                        <h3 className="text-white font-semibold mb-2">Delete Transcript</h3>
                        <p className="text-gray-300 text-sm mb-4">Are you sure you want to delete this transcript? This will remove it from all participants' histories and cannot be undone.</p>
                        <div className="flex gap-3">
                            <button onClick={() => setShowConfirmDelete(false)} className="flex-1 px-4 py-2 rounded border border-gray-700 text-gray-300 hover:bg-gray-800 transition-colors">Cancel</button>
                            <button onClick={handleDeleteEvent} className="flex-1 px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 transition-colors">Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {notification && (<NotificationToast notification={notification} onClose={() => setNotification(null)} />)}
        </div>
    );
}